import { Identifier, Timestamp } from 'src/types/utilityTypes';

import { SubscriptionCoupon } from '../coupon/_types';
import { Package } from '../package/_types';

import type { User } from 'src/models/user/_types';

export interface Subscription {
  id: string;
  status:
    | 'active'
    | 'past_due'
    | 'canceled'
    | 'incomplete'
    | 'incomplete_expired';
  latestInvoice: string;
  createdAt: Timestamp;
  endedAt: Timestamp;
  paidById: Identifier;
  plan: PaymentPlan;
  startOn: Date;
  subscribeableId: Identifier;
  subscribeableType: string;
  package?: Package;
  subId: Identifier;
  currentPeriodEnd?: Date | null;
  currentPeriodStart?: Date | null;
}

export enum SubscriptionInterval {
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Year = 'year',
}

export const SubscriptionIntervalDisplay: Record<SubscriptionInterval, string> =
  {
    [SubscriptionInterval.Day]: 'Day(s)',
    [SubscriptionInterval.Week]: 'Week(s)',
    [SubscriptionInterval.Month]: 'Month(s)',
    [SubscriptionInterval.Year]: 'Year(s)',
  };

export enum ProrationBehavior {
  Prorate = 'prorate',
  NoProrate = 'no_prorate',
  NoCharge = 'no_charge',
}

export const ProrationBehaviorDisplay: Record<ProrationBehavior, string> = {
  [ProrationBehavior.Prorate]: 'Prorate First Charge',
  [ProrationBehavior.NoProrate]: 'Charge Full Amount',
  [ProrationBehavior.NoCharge]: 'Do Not Charge',
};

export enum ContractEndBehavior {
  Cancel = 'cancel',
  AutoRenew = 'auto_renew',
}

export const ContractEndBehaviorDisplay: Record<ContractEndBehavior, string> = {
  [ContractEndBehavior.Cancel]: 'Cancel',
  [ContractEndBehavior.AutoRenew]: 'Auto Renew',
};

export interface PaymentPlanData {
  id: string;
  tiers: any;
  active: boolean;
  amount: number;
  object: string;
  created: number;
  product: string;
  currency: string;
  interval: SubscriptionInterval;
  livemode: boolean;
  nickname: string;
  tiersMode: any;
  usageType: string;
  amountDecimal: string;
  billingScheme: string;
  intervalCount: number;
  aggregateUsage: any;
  transformUsage: any;
  trialPeriodDays: any;
  oneOffPaymentPlan: any;
  metadata?: {
    prorate?: ProrationBehavior;
    noProrate?: boolean;
    cancelAfterBehavior?: ContractEndBehavior;
    cancelAfter?: number;
    billingCycleAnchor?: string;
  };
}

export interface PaymentPlan {
  id: Identifier;
  name: string;
  details: PaymentPlanData;
}

export interface PriceBreakdown {
  total: string;
  amountRefunded: string;
  tax: string;
}

export interface PurchaseItem {
  id: Identifier;
  itemType: string;
  itemId: Identifier;
  name: string;
  chargeId: string;
  purchaseId: Identifier | null;
  subscriptionId: Identifier | null;
  cartItemId: Identifier | null;
  priceBreakdown: PriceBreakdown | null;
  feeBreakdown: any | null;
  refundNotes: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface Charge {
  id: Identifier;
  amount: number;
  cartId: Identifier | null;
  userId: User['id'];
  tax: number;
  fee: number;
  net: number;
  amountRefunded: number;
  amountRefundedFee: number;
  paidAt: Timestamp;
  name: string;
  email: string;
  itemName: string;
  paymentPlan: string | null;
  status: string;
  paymentMethod: string;
  failureCode: string | null;
  failureMessage: string | null;
  paidById: Identifier | null;
  paidByName: string | null;
  paidForId: Identifier | null;
  paidForName: string | null;
  purchaseId: Identifier | null;
  subscriptionId: Identifier | null;
  processorId: string;
  currency: string;
  checkNumber: string | null;
  externalPaymentMethod: any | null;
  paymentMethodDetails: any | null;
  receiptUrl: string | null;
  receiptToken: string | null;
  couponCode: string | null;
  discount: number;
  description: string | null;
  startingBalance: number;
  endingBalance: number;
  balanceAdjustment: number;
  stripeObjectId: string | null;
  receiptNotes: string | null;
  refundNotes: string;
  processorType: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  purchaseItems: PurchaseItem[];
  guestEmail: string | null;
  guestFirstName: string | null;
  guestLastName: string | null;
  disputeStatus: string | null;
  disputeReason: string | null;
}

export interface Invoice {
  id: Identifier;
  platformId: number;
  userId: User['id'];
  paidById: User['id'];
  paidBy?: User;
  paidByName?: string;
  itemName: string;
  itemType: string;
  itemId: number | null;
  autoAdvance: boolean;
  collectionMethod: string;
  amountDue?: number;
  amountPaid: number;
  attemptCount: number;
  description: string;
  subscriptionId: string | null;
  chargeId: string | null;
  status: string;
  applicationFeeAmount: number;
  currency: string;
  paidAt: Timestamp | null;
  dueDate: Date;
  nextPaymentAttempt: Timestamp | null;
  couponCode: string | null;
  accountId: string;
  destinationType: string;
  destinationId: number;
  taxPercent?: number;
  name: string;
  email: string;
  customFields: any[];
  hostedInvoiceUrl: string;
  invoicePdf: string;
  clientId?: number;
  chargeDate: Date | null;
  paymentMethodId: Identifier;
  chargeFailure: any | null;
  lastChargeAttempt: Timestamp | null;
  startingBalance: number;
  endingBalance: number;
  balanceAdjustment: number;
  autoAssignItem: boolean;
  itemDelivered: boolean;
  paidOutOfBand: boolean;
  manual: boolean;
  chargeStatus: string;
  transactionIds: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // used when saving an invoice
  invoiceAction?: 'void' | 'draft' | 'pay';
}

export interface CustomerBalanceTransaction {
  id: string;
  platformId: number;
  userId: User['id'];
  itemName: string | null;
  itemType: string | null;
  itemId: number | null;
  amount: number;
  startingBalance: number;
  endingBalance: number;
  description: string;
  processorId: string;
  processorType: string;
  currency: string;
  name: string;
  email: string;
  clientId: number;
  createdAt: Timestamp;
  createdById: number;
  createdByName: string;
  updatedAt: Timestamp;
}

export interface ServiceBalanceTransaction {
  id: string;
  platformId: number;
  userId: User['id'];
  itemType: string;
  itemId: number;
  amount: number;
  startingBalance: number;
  endingBalance: number;
  description: string;
  name: string;
  email: string;
  clientId: number;
  createdAt: Timestamp;
  createdById: number;
  createdByName: string;
  invoiceProcessorId: string | null;
  invoiceItemProcessorId: string | null;
  invoiceDescription: string | null;
  updatedAt: Timestamp;
  subscriptionId: string | null;
}

export interface StripePlan {
  id: string;
  name: string;
  active: boolean;
  amount: number;
  object: string;
  created: number;
  product: string;
  currency: string;
  interval: string;
  livemode: boolean;
  metadata: {
    platformId: string;
    platformBrand: string;
    prorate: ProrationBehavior;
    noProrate: boolean;
    billingCycleAnchor: string;
    oneOffPaymentPlan: boolean;
    cancelAfterBehavior: ContractEndBehavior;
    cancelAfter?: number;
  };
  nickname: string;
  tiersMode: string | null;
  usageType: string;
  amountDecimal: string;
  billingScheme: string;
  intervalCount: number;
  aggregateUsage: string | null;
  transformUsage: string | null;
  trialPeriodDays: number | null;
}

interface StripePrice extends StripePlan {
  type: string;
  recurring: {
    interval: string;
    usageType: string;
    intervalCount: number;
    aggregateUsage: string | null;
    trialPeriodDays: number | null;
  };
  lookupKey: string | null;
  taxBehavior: string;
  customUnitAmount: string | null;
  transformQuantity: string | null;
  unitAmountDecimal: string;
}

interface StripeSubscriptionItem {
  id: string;
  plan: StripePlan;
  price: StripePrice;
  object: string;
  created: number;
  metadata: Record<string, unknown>;
  quantity: number;
  taxRates: any[];
  subscription: string;
  billingThresholds: any | null;
}

interface StripeTaxRate {
  id: string;
  state: string | null;
  active: boolean;
  object: string;
  country: string | null;
  created: number;
  livemode: boolean;
  metadata: Record<string, unknown>;
  taxType: string | null;
  inclusive: boolean;
  percentage: number;
  description: string | null;
  displayName: string;
  jurisdiction: string | null;
  jurisdictionLevel: string | null;
  effectivePercentage: number | null;
}

export type NextBillingKind =
  | 'next'
  | 'cancels'
  | 'resumes'
  | 'paused_indefinitely';

export interface StripeSubscription {
  id: string;
  plan: StripePlan;
  items: {
    url: string;
    data: StripeSubscriptionItem[];
    object: string;
    hasMore: boolean;
    totalCount: number;
  };
  object: string;
  status: string;
  created: number;
  currency: string;
  customer: string;
  clientId?: number;
  discount: any | null;
  endedAt: number | null;
  livemode: boolean;
  metadata: Record<string, unknown>;
  quantity: number;
  schedule: any | null;
  cancelAt: number | null;
  trialEnd: number | null;
  startDate: number;
  testClock: any | null;
  application: any | null;
  canceledAt: number | null;
  description: string | null;
  trialStart: number | null;
  onBehalfOf: string;
  automaticTax?: {
    enabled: boolean;
  };
  transferData: {
    destination: string;
    amountPercent: number | null;
  };
  daysUntilDue: number | null;
  defaultSource: string | null;
  latestInvoice: string;
  pendingUpdate: any | null;
  trialSettings: {
    endBehavior: {
      missingPaymentMethod: string;
    };
  };
  pauseCollection: {
    behavior: string;
    resumes_at?: Date | null;
  };
  paymentSettings: {
    paymentMethodTypes: string[];
    paymentMethodOptions: any | null;
    saveDefaultPaymentMethod: string;
  };
  collectionMethod: string;
  defaultTaxRates: StripeTaxRate[];
  billingThresholds: any | null;
  currentPeriodEnd?: Date | null;
  billingCycleAnchor: number;
  cancelAtPeriodEnd: boolean;
  cancellationDetails: {
    reason: string;
    comment: string | null;
    feedback: string | null;
  };
  currentPeriodStart?: Date | null;
  pendingSetupIntent: any | null;
  defaultPaymentMethod: string;
  applicationFeePercent: number;
  billingCycleAnchorConfig: any | null;
  pendingInvoiceItemInterval: any | null;
  nextPendingInvoiceItemInvoice: any | null;
  paidById: number;
  paidByName: string;
  upgradePaymentType: string | null;
  accountId: string;
  destinationId: number;
  destinationType: string;
  destinationName: string;
  upgradeCoupon: SubscriptionCoupon | null;
  upgradePlan: StripePlan | null;
  upgradeProrationType: ProrationBehavior | null;
  startOn: Date | null;
  subId: Identifier;
  totalPayments: number;
  cancelOn: string;
  cancelOnPeriod: string | null;
  isCouponPaused: boolean;
  couponResumeOn?: Date | null;
  couponPauseOn?: Date | null;
  pauseCouponCode: string | null;
  stripePauseOn?: Date | null;
  freezePause: boolean;
  stripeResumeAt?: Date | null;
  resetBillingCycleAnchor: boolean;
  resumeProrateBehavior: string | null;
  incomplete: boolean | null;
  paymentIntentSecret: string;
  paymentIntentStatus: string | null;
  paymentMethodType: string;
  paymentMethodLast4: string;
  paymentMethodBrand: string;
  pauseInvoiceId: string;
  pausedAt: number | null;
  upgradeOn: Date | null;
  upgradeProratedBehavior: string | null;
  cancelAfter: number | null;
  contractStartDate?: Date | null;
  nextBillingDate?: number | null;
  nextBillingKind?: NextBillingKind | null;
  taxPercent: number;
  userId: User['id'];
  iap?: boolean;

  upgradeProrationBehavior:
    | 'none'
    | 'always_invoice'
    | 'none_with_trial';
  subscribeableId: Identifier | null;
  subscribeableType: 'Group' | 'Fbm::Package' | 'ClientAssociation';
  subscribeableName: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
}

export interface AutoRenew {
  id: string;
  itemName: string;
  startOn?: string;
  startedAt?: string;
  currentCycles: number;
  maxCycles: number | null;
  errorMessage?: string;
  user: User;
}

export type ResumeProrateBehavior =
  | 'no_charge'
  | 'no_prorate'
  | 'credit_unused_time';

export interface SubscriptionReportParams {
  per?: number;
  page?: number;
  rangeType?: string;
  couponCode?: string;
  subscribeableId?: number | 'null';
  subscribeableType?: string;
  userSearch?: string;
  startOnNotNull?: boolean;
  inactive?: boolean;
  willCancel?: boolean;
  isPaused?: boolean;
  startDate?: number;
  endDate?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  pastDue?: boolean;
}

export interface UpcomingCharge {
  amount: number;
  currency: string;
  date: number;
  email: string;
  user: string;
}

export interface UpcomingChargeResponse {
  meta: {
    total: number;
  };
  upcomingCharges: UpcomingCharge[];
  upcomingTotal: number;
}

export interface FetchChargesResponse {
  charges: Charge[];
  meta: {
    total: number;
    sum: number;
  };
}

export interface BusinessSubscription {
  plan_id: string;
  subscription_id: string;
  subscribeable: {
    id: number;
    name: string;
    type: string;
  };
  user: {
    id: number;
    email: string;
    name: string;
  };
  created_at: number;
  updated_at: number;
  coupon_code: string | null;
  name: string;
  cancel_date: string | null;
  paused: boolean;
  start_on: string | null;
  pause_on: string | null;
  client_id: number;
  cancel_on: string | null;
  tax_percent: string;
  subscription: StripeSubscription;
}

export interface Payouts {
  available: number;
  supported?: boolean;
  fee: number;
  instantEnabled: boolean;
  usdAccount: boolean;
  supportsInstant: boolean;
  availableInstant: number;
  instantFee: number;
  instantMinimum: number;
  balance: {
    object: string;
    available: {
      amount: number;
      currency: string;
      source_types: {
        card: number;
      };
    }[];
    instantAvailable: {
      amount: number;
      currency: string;
      sourceTypes: {
        card: number;
      };
    }[];
    livemode: boolean;
    pending: {
      amount: number;
      currency: string;
      sourceTypes: {
        card: number;
      };
    }[];
  };
}
